import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { FlagsProvider } from '@atlaskit/flag';
import type { TriggerProps } from '@atlaskit/popup';
import { Text, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import {
	AIEventsInstrumentationProvider,
	type AIEventsInstrumentationConfig,
} from '@atlassian/ai-analytics';

import { expVal } from '@confluence/feature-experiments';
import { SmartsFeedbackComponent } from '@confluence/quick-summary/entry-points/SmartsFeedbackComponent.ts';
import type { SummaryAnalyticsPropertiesType } from '@confluence/quick-summary/entry-points/summarySharedTypes.ts';
import { FeedbackCollectorPropertiesProvider } from '@confluence/quick-summary/entry-points/FeedbackCollectorPropertiesProvider.ts';
import { FeatureCode } from '@confluence/feature-codes/entry-points/FeatureCodeModel.ts';

import { EndOfPageRecommendationInfoPopup } from './EndOfPageRecommendationInfoPopup.tsx';

const i18n = defineMessages({
	headerTitle: {
		id: 'page-recommendations.end-of-page-recommendation-compact-header.title',
		defaultMessage: 'Related content',
		description: 'The title of the end of page recommendation compact header.',
	},
	feedbackTitle: {
		id: 'page-recommendations.end-of-page-recommendation-compact-header.feedback.title',
		defaultMessage: 'Help us improve Confluence',
		description: 'Label text for the summary section of the report feedback collector',
	},
	feedbackSummaryPlaceholder: {
		id: 'page-recommendations.end-of-page-recommendation-compact-header.feedback.summary.placeholder',
		defaultMessage: 'What did we do well? What could be improved?',
		description: 'Description text for the summary section of the report feedback collector',
	},
});
const styles = cssMap({
	container: {
		paddingBottom: token('space.200'),
	},
});

type EndOfPageRecommendationCompactHeaderProps = {
	toggleInfo: () => void;
	isInfoOpen: boolean;
	handleInfoButtonRef: (triggerProps: TriggerProps) => (node: HTMLButtonElement | null) => void;
	analyticsProperties: SummaryAnalyticsPropertiesType;
	contentId: string;
};

export const EndOfPageRecommendationCompactHeader = ({
	toggleInfo,
	isInfoOpen,
	handleInfoButtonRef,
	analyticsProperties,
	contentId,
}: EndOfPageRecommendationCompactHeaderProps) => {
	const { formatMessage } = useIntl();

	const aiAnalyticsConfig: AIEventsInstrumentationConfig = useMemo(
		() => ({
			product: 'confluence',
			subproduct: 'confluence',
			source: 'endOfPageRecommendationHeader',
			aiFeatureName: 'endOfPageRecommendation',
			proactiveGeneratedAI: 1,
			userGeneratedAI: 0,
			additionalAttributes: {
				contentId,
				contentType: analyticsProperties?.additionalAnalyticsAttributes?.contentType,
				from: FeatureCode.EOP_REC,
			},
		}),
		[analyticsProperties?.additionalAnalyticsAttributes?.contentType, contentId],
	);

	const feedbackComponent = (
		<FlagsProvider>
			<FeedbackCollectorPropertiesProvider entrypointId="d15eac98-e451-452a-84ce-dcf70888cb52">
				<AIEventsInstrumentationProvider config={aiAnalyticsConfig}>
					<SmartsFeedbackComponent
						analyticsProperties={analyticsProperties}
						contentId={contentId}
						feedbackTitle={i18n.feedbackTitle}
						feedbackSummaryPlaceholder={i18n.feedbackSummaryPlaceholder}
						aiContentType="suggestion"
					/>
				</AIEventsInstrumentationProvider>
			</FeedbackCollectorPropertiesProvider>
		</FlagsProvider>
	);

	return (
		<Inline
			alignBlock="center"
			spread="space-between"
			xcss={styles.container}
			testId="eop-compact-header"
		>
			<Inline space="space.100" alignBlock="center">
				<Text size="medium" weight="medium">
					{formatMessage(i18n.headerTitle)}
				</Text>
				<EndOfPageRecommendationInfoPopup
					toggleInfo={toggleInfo}
					showFeedbackButton={false}
					isInfoOpen={isInfoOpen}
					handleInfoButtonRef={handleInfoButtonRef}
				/>
				{expVal('confluence_related_pages_remove_feedback_icons', 'isEnabled', false)
					? null
					: feedbackComponent}
			</Inline>
		</Inline>
	);
};
